import { Spin } from "antd";
import i18n from "i18next";
import { type FC, useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import { FormManager } from "../../../../../components/Form/FormManager";
import { useFormSubmitCompletion } from "../../../../../components/Form/hooks/useFormSubmitCompletion";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { ResourceName, ResourceVariant } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../core/router/types/routerTypes";
import { showWarningNotification } from "../../../../../core/setup/redux/state/notifications/notificationsState";
import type { FormJson } from "../../../../../core/types/FormJsonTypes";
import { useCampaignSaveAndGenerate } from "../../hooks/useCampaignSaveAndGenerate";
import { useCampaignSaveMutation } from "../../services/server/CampaignApi";
import type { CampaignEntity } from "../../types/CampaignEntity";
import campaignCreateJson from "./campaign-orgunits-create.json";

export const CampaignOrgUnitsCreateForm: FC<FormProps & { terminals1?: string[]; sites1?: string[] }> = ({
    runSubmit,
    formAction,
    terminals1,
    sites1,
}) => {
    const formDefinition = campaignCreateJson as FormJson;

    const [formData, setFormData] = useState<CampaignEntity>();
    const onChangeCallback = useCallback(setFormData, [setFormData]);

    const dispatch = useDispatch();
    const { saveAndGenerate, isSaveInProgress } = useCampaignSaveAndGenerate(useCampaignSaveMutation);
    const completeSubmit = useFormSubmitCompletion(formAction, `${BASE_PATH}/${ResourceName.CAMPAIGNS}`);

    // Get default values from form definition
    // and
    // Apply terminals1 and sites1 to formData if present
    useEffect(() => {
        if (formDefinition?.formConfiguration) {
            const initialValues: CampaignEntity = {} as CampaignEntity;

            formDefinition.formConfiguration.tabs.forEach((tab) => {
                tab.fields.forEach((field) => {
                    const defaultValue = field.attributes?.defaultValue;
                    if (defaultValue !== undefined) {
                        (initialValues as Record<string, unknown>)[field.name] = defaultValue;
                    }
                });
            });

            if (terminals1 !== undefined) {
                initialValues.terminals1 = terminals1;
            }
            if (sites1 !== undefined) {
                initialValues.sites1 = sites1;
            }

            setFormData(initialValues as CampaignEntity);
        }
    }, [formDefinition, terminals1, sites1]);

    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            // is at least one terminal selected?
            const selectedTerminalIds = formData.terminals1;
            const selectedTerminalIds2 = formData.terminals2;
            if (!selectedTerminalIds || selectedTerminalIds.length === 0) {
                dispatch(
                    showWarningNotification({
                        text: i18n.t("campaign.error.required"),
                        autoCloseDisabled: true,
                    }),
                );
                return;
            }
            if (formData.terminals2 && (!selectedTerminalIds2 || selectedTerminalIds2.length === 0)) {
                dispatch(
                    showWarningNotification({
                        text: i18n.t("campaign.error.required"),
                        autoCloseDisabled: true,
                    }),
                );
                return;
            }

            const result = await saveAndGenerate(formData, formAction);

            const hasError = "error" in result;
            if (!hasError) {
                const successText =
                    formAction === FORM_ACTION.GENERATE
                        ? i18n.t("success.campaign.generate.successfully", { 0: result.data.id })
                        : i18n.t("common.formSaveSuccess", { id: result.data.id, resource: "Campaign" });
                completeSubmit(
                    successText,
                    `${BASE_PATH}/${ResourceName.CAMPAIGNS}/${ResourceVariant.ORG_UNITS}/${FORM_ACTION.EDIT}/${result.data.id}`,
                );
            }
        },
        [saveAndGenerate, formData, formAction, completeSubmit, dispatch],
    );

    return (
        <Spin spinning={isSaveInProgress}>
            {formDefinition?.formConfiguration && formData && (
                <FormManager<CampaignEntity>
                    name="CampaignCreateForm"
                    formData={formData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
